import React, { useState } from 'react';
import { SearchIcon, FilterIcon, ChevronDownIcon, ChevronUpIcon, ArrowRightIcon, AlertTriangleIcon, FileTextIcon, DownloadIcon, ExternalLinkIcon } from 'lucide-react';
import StatusBadge from '../components/StatusBadge';
const PolicyAnalysis = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [expandedPolicy, setExpandedPolicy] = useState<number | null>(1); // Start with first policy expanded
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card');
  const [selectedPayer, setSelectedPayer] = useState<string | null>(null);
  const [showComparisonView, setShowComparisonView] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const toggleExpand = (id: number) => {
    if (expandedPolicy === id) {
      setExpandedPolicy(null);
    } else {
      setExpandedPolicy(id);
    }
  };
  // Sample data
  const policies = [{
    id: 1,
    type: 'Active',
    status: 'negative',
    payer: 'Aetna',
    insuredLives: '25,287,393',
    title: 'Electrical Stimulation for Pain',
    policyNumber: '0011',
    updated: '05/19/2025',
    effectiveDate: '04/14/2025',
    reviewDate: '01/08/2026',
    previousStatus: 'restricted',
    changeHighlights: ['Added Freedom PNS System to list of experimental devices', 'Removed coverage for peripheral nerve field stimulation', 'Added stricter criteria for all neurostimulation devices'],
    coverageStatement: 'The following durable medical equipment (DME) and supplies are considered experimental, investigational, or unproven because the effectiveness of these approaches for the specified indication(s) has not been established (not an all-inclusive list): Peripheral subcutaneous field stimulation (PSFS) or peripheral nerve field stimulation (PNFS) (e.g., the Freedom PNS System, the Sprint PNS System, the StimQ PNS System) for the treatment of chronic pain, hemiplegic shoulder pain, and other indications (e.g., angina, notalgia paresthetica).',
    codes: '64555 listed in policy.',
    impact: 'high',
    marketImpact: 'This policy change affects approximately 25.3M lives across 38 states. The restriction of coverage for peripheral nerve stimulation represents a significant market barrier in the Northeast and Mid-Atlantic regions where Aetna has strong market penetration.',
    recommendations: ['Submit clinical evidence package by July 15', 'Request policy reconsideration meeting', 'Develop physician advocacy strategy']
  }, {
    id: 2,
    type: 'Active',
    status: 'negative',
    payer: 'AmeriHealth New Jersey',
    insuredLives: '141,183',
    title: 'Experimental/Investigational Services',
    policyNumber: '12.01.01bp',
    updated: '05/05/2025',
    effectiveDate: '04/01/2025',
    reviewDate: '01/01/2026',
    previousStatus: 'negative',
    changeHighlights: ['Added CPT code 64590 to list of non-covered services', 'No substantive changes to coverage criteria'],
    coverageStatement: 'This list represents services that are considered E/I and are represented by a CPT or HCPCS code that can be reported for other services.',
    codes: '64555, 64590 listed in policy.',
    impact: 'low',
    marketImpact: 'Limited impact due to small membership base and existing negative coverage. This change primarily affects documentation and coding.',
    recommendations: ['Monitor for regional influence on larger payers', 'No immediate action recommended']
  }, {
    id: 3,
    type: 'Active',
    status: 'no-policy',
    payer: 'AvMed',
    insuredLives: '181,361',
    title: '',
    policyNumber: '',
    updated: '',
    effectiveDate: '',
    reviewDate: '',
    previousStatus: 'no-policy',
    changeHighlights: [],
    coverageStatement: '',
    codes: '',
    impact: 'medium',
    marketImpact: 'Opportunity to establish coverage through medical necessity determinations. No formal policy creates case-by-case review potential.',
    recommendations: ['Develop medical director engagement strategy', 'Prepare case-by-case review support materials for providers']
  }, {
    id: 4,
    type: 'Active',
    status: 'positive',
    payer: 'BCBS Alabama',
    insuredLives: '2,533,935',
    title: 'Peripheral Nerve Stimulation of the Trunk or Limbs for Treatment of Pain',
    policyNumber: '623',
    updated: '10/01/2024',
    effectiveDate: '10/01/2024',
    reviewDate: '10/01/2025',
    previousStatus: 'no-policy',
    changeHighlights: ['New positive coverage policy established', 'Covers peripheral nerve stimulation for chronic pain', 'Includes specific clinical criteria for coverage'],
    coverageStatement: 'Peripheral nerve stimulation of the trunk or limbs may be considered medically necessary for the treatment of chronic intractable pain that is refractory to all other pain therapies, when ALL of the following criteria, clearly documented in the individuals record, are met: (See policy for criteria)',
    codes: '64555, 64575, 64590, 64595',
    impact: 'high',
    marketImpact: 'Significant positive development establishing coverage for 2.5M lives. This policy may influence other BCBS plans in the Southeast region.',
    recommendations: ['Develop provider education program for Alabama', 'Create case studies highlighting successful patient outcomes', 'Leverage policy language for advocacy with other BCBS plans']
  }, {
    id: 5,
    type: 'Active',
    status: 'positive',
    payer: 'BCBS Tennessee',
    insuredLives: '3,245,982',
    title: 'Peripheral Nerve Stimulation',
    policyNumber: 'MED.00123',
    updated: '11/20/2024',
    effectiveDate: '12/15/2024',
    reviewDate: '11/20/2025',
    previousStatus: 'restricted',
    changeHighlights: ['Expanded coverage criteria to include more indications', 'Removed requirement for trial period', 'Added coverage for specific nerve applications'],
    coverageStatement: 'Peripheral nerve stimulation is considered medically necessary for the treatment of chronic, intractable pain when specific criteria are met.',
    codes: '64555, 64575, 64590, 64595',
    impact: 'medium',
    marketImpact: 'Positive coverage expansion in a key southeastern market. May influence other regional BCBS plans.',
    recommendations: ['Leverage policy language for advocacy with other BCBS plans', 'Develop provider education materials specific to Tennessee']
  }, {
    id: 6,
    type: 'Active',
    status: 'negative',
    payer: 'BCBS Michigan',
    insuredLives: '4,567,210',
    title: 'Peripheral Subcutaneous Field Stimulation And Peripheral Nerve Stimulation',
    policyNumber: 'SUR.8.01',
    updated: '03/15/2025',
    effectiveDate: '04/01/2025',
    reviewDate: '03/15/2026',
    previousStatus: 'negative',
    changeHighlights: ['Annual review with no substantive changes', 'Maintained experimental classification'],
    coverageStatement: 'Peripheral subcutaneous field stimulation is considered experimental and investigational for all indications including but not limited to the treatment of back pain, neck pain, headache, abdominal pain, pelvic pain, and post-herpetic neuralgia.',
    codes: '64555, 64575, 64590, 64595, 64999',
    impact: 'medium',
    marketImpact: 'Continued negative coverage in a major Midwest market affecting 4.5M lives.',
    recommendations: ['Submit updated clinical evidence package', 'Engage with medical directors']
  }, {
    id: 7,
    type: 'Active',
    status: 'restricted',
    payer: 'Cigna',
    insuredLives: '17,852,631',
    title: 'Nerve Stimulation Devices for the Treatment of Pain',
    policyNumber: '0369',
    updated: '06/15/2025',
    effectiveDate: '07/01/2025',
    reviewDate: '06/15/2026',
    previousStatus: 'negative',
    changeHighlights: ['Added limited coverage with significant restrictions', 'Requires specific neuropathic pain diagnoses', 'Requires minimum 6-month conservative therapy trial'],
    coverageStatement: 'Peripheral nerve field stimulation (PNFS) is considered medically necessary for an individual with chronic intractable neuropathic pain when ALL of the following criteria are met: (see policy for specific criteria)',
    codes: '64555, 64575, 64590, 64595',
    impact: 'high',
    marketImpact: 'Significant improvement from previous negative policy, though with substantial restrictions. Affects nearly 18M lives nationwide.',
    recommendations: ['Develop provider education on meeting criteria', 'Monitor implementation and denial patterns']
  }, {
    id: 8,
    type: 'Active',
    status: 'negative',
    payer: 'UnitedHealthcare',
    insuredLives: '31,742,506',
    title: 'Electrical Stimulation for Pain Management',
    policyNumber: '2025T0123Z',
    updated: '03/20/2025',
    effectiveDate: '05/01/2025',
    reviewDate: '03/20/2026',
    previousStatus: 'negative',
    changeHighlights: ['Updated criteria with more restrictive language', 'Added explicit exclusion for peripheral nerve field stimulation', 'Added reference to Freedom PNS System as investigational'],
    coverageStatement: 'Peripheral nerve stimulation (PNS) and peripheral nerve field stimulation (PNFS) are unproven and not medically necessary for treating pain due to insufficient evidence of efficacy.',
    codes: '64555, 64575, 64590, 64595',
    impact: 'high',
    marketImpact: "Major negative impact affecting over 31M lives nationwide. UHC's policy often influences other commercial payers.",
    recommendations: ['Prioritize engagement with UHC medical directors', 'Submit comprehensive evidence package']
  }, {
    id: 9,
    type: 'Active',
    status: 'positive',
    payer: 'BCBS Wyoming',
    insuredLives: '123,456',
    title: 'Implantable Peripheral Nerve Stimulation for Chronic Pain Conditions',
    policyNumber: 'SUR.712',
    updated: '11/15/2024',
    effectiveDate: '01/01/2025',
    reviewDate: '11/15/2025',
    previousStatus: 'no-policy',
    changeHighlights: ['New positive coverage policy established', 'Covers PNS for specific chronic pain conditions', 'Requires documented failure of conservative treatments'],
    coverageStatement: 'Implantable peripheral nerve stimulation may be medically necessary when ALL of the following criteria are met: 1) The patient has chronic, intractable pain resulting from a peripheral nerve injury; 2) Other treatment modalities have been tried and failed...',
    codes: '64555, 64575, 64590, 64595',
    impact: 'low',
    marketImpact: 'Positive policy but limited impact due to small membership base. May provide model language for other BCBS plans.',
    recommendations: ['Use as model policy for other BCBS plans', 'Develop case studies from Wyoming implementation']
  }, {
    id: 10,
    type: 'Active',
    status: 'negative',
    payer: 'Anthem',
    insuredLives: '22,654,789',
    title: 'Peripheral Nerve and Peripheral Nerve Field Stimulation',
    policyNumber: 'SURG.00158',
    updated: '03/01/2025',
    effectiveDate: '04/15/2025',
    reviewDate: '03/01/2026',
    previousStatus: 'negative',
    changeHighlights: ['Policy remains investigational for all indications', 'Updated references but no substantive changes'],
    coverageStatement: 'Peripheral nerve stimulation or peripheral nerve field stimulation is considered investigational and not medically necessary for all indications.',
    codes: '64555, 64575, 64590, 64595, 64999',
    impact: 'high',
    marketImpact: 'Continued negative coverage affecting over 22M lives across multiple states.',
    recommendations: ['Engage with regional medical directors', 'Submit updated clinical evidence package']
  }, {
    id: 11,
    type: 'Active',
    status: 'restricted',
    payer: 'Humana',
    insuredLives: '16,987,432',
    title: 'Peripheral Nerve Stimulation',
    policyNumber: 'HCS-0496-013',
    updated: '04/15/2025',
    effectiveDate: '06/01/2025',
    reviewDate: '04/15/2026',
    previousStatus: 'no-policy',
    changeHighlights: ['New coverage policy with specific criteria for PNS', 'Requires extensive documentation and prior authorization', 'Limited to specific nerve applications'],
    coverageStatement: 'Humana members may be eligible under the Plan for peripheral nerve stimulation (PNS) when specific criteria are met.',
    codes: '64555, 64575, 64590, 64595',
    impact: 'high',
    marketImpact: 'New policy with restricted coverage affecting nearly 17M lives, primarily in Medicare Advantage.',
    recommendations: ['Develop provider education on documentation requirements', 'Monitor implementation and appeal success rates']
  }, {
    id: 12,
    type: 'Active',
    status: 'negative',
    payer: 'BCBS Louisiana',
    insuredLives: '1,789,543',
    title: 'Implantable Peripheral Nerve Stimulation Devices as a Treatment for Pain',
    policyNumber: '00276',
    updated: '05/15/2025',
    effectiveDate: '06/15/2025',
    reviewDate: '05/15/2026',
    previousStatus: 'negative',
    changeHighlights: ['Annual review with no substantive changes', 'Maintained investigational classification'],
    coverageStatement: 'Based on review of available data, the Company considers implantable peripheral nerve stimulation devices for the treatment of pain to be investigational.',
    codes: '64555, 64575, 64590, 64595',
    impact: 'medium',
    marketImpact: 'Continued negative coverage in Louisiana market, affecting approximately 1.8M lives.',
    recommendations: ['Monitor for regional BCBS policy variations', 'Consider targeted clinical evidence submission']
  }, {
    id: 13,
    type: 'Active',
    status: 'no-policy',
    payer: 'Centene',
    insuredLives: '12,345,678',
    title: '',
    policyNumber: '',
    updated: '',
    effectiveDate: '',
    reviewDate: '',
    previousStatus: 'no-policy',
    changeHighlights: [],
    coverageStatement: '',
    codes: '',
    impact: 'high',
    marketImpact: 'Significant opportunity with large Medicaid managed care organization. Case-by-case determinations likely.',
    recommendations: ['Develop medical director engagement strategy', 'Prepare case-by-case review support materials', 'Monitor for policy development']
  }, {
    id: 14,
    type: 'Active',
    status: 'negative',
    payer: 'Health Net',
    insuredLives: '3,456,789',
    title: 'Implantable Electrical Stimulators',
    policyNumber: 'DME-0080',
    updated: '10/01/2024',
    effectiveDate: '11/15/2024',
    reviewDate: '10/01/2025',
    previousStatus: 'negative',
    changeHighlights: ['Maintained non-coverage position for PNS', 'Updated references but no substantive changes'],
    coverageStatement: 'Peripheral nerve stimulation is considered investigational for all indications.',
    codes: '64555, 64590',
    impact: 'medium',
    marketImpact: 'Continued negative coverage affecting approximately 3.5M lives, primarily in Western states.',
    recommendations: ['Low priority for engagement based on historical resistance', 'Monitor for policy updates']
  }, {
    id: 15,
    type: 'Active',
    status: 'restricted',
    payer: 'Kaiser Permanente',
    insuredLives: '12,456,789',
    title: 'Peripheral Nerve Stimulation for Pain',
    policyNumber: 'PNSTP025',
    updated: '01/15/2025',
    effectiveDate: '03/01/2025',
    reviewDate: '01/15/2026',
    previousStatus: 'negative',
    changeHighlights: ['New restricted coverage policy', 'Limited to specific nerve applications and diagnoses', 'Requires extensive documentation'],
    coverageStatement: 'Peripheral nerve stimulation may be considered medically necessary for select patients with chronic, intractable pain when specific criteria are met.',
    codes: '64555, 64575, 64590',
    impact: 'high',
    marketImpact: 'Improvement from previous negative policy. Affects over 12M lives in key Western markets.',
    recommendations: ['Develop provider education specific to Kaiser requirements', 'Monitor implementation and appeal success rates']
  }, {
    id: 16,
    type: 'Active',
    status: 'negative',
    payer: 'Molina Healthcare',
    insuredLives: '5,123,456',
    title: 'Pain Management Interventions',
    policyNumber: 'MCP-374',
    updated: '12/01/2024',
    effectiveDate: '01/15/2025',
    reviewDate: '12/01/2025',
    previousStatus: 'no-policy',
    changeHighlights: ['New policy explicitly excluding PNS for chronic pain', 'Classifies as experimental/investigational'],
    coverageStatement: 'Peripheral nerve stimulation is considered experimental, investigational and unproven for the treatment of chronic pain.',
    codes: '64555, 64575, 64590, 64595',
    impact: 'medium',
    marketImpact: 'New negative policy affecting over 5M lives, primarily in Medicaid managed care.',
    recommendations: ['Engage with medical directors', 'Develop Medicaid-specific value proposition']
  }, {
    id: 17,
    type: 'Active',
    status: 'restricted',
    payer: 'BCBS North Carolina',
    insuredLives: '3,789,456',
    title: 'Peripheral Nerve Stimulation',
    policyNumber: 'MED1234',
    updated: '11/01/2024',
    effectiveDate: '01/01/2025',
    reviewDate: '11/01/2025',
    previousStatus: 'negative',
    changeHighlights: ['Added limited coverage with significant restrictions', 'Requires specific pain diagnoses and specialist evaluation'],
    coverageStatement: 'Peripheral nerve stimulation may be considered medically necessary for a limited set of indications when specific criteria are met.',
    codes: '64555, 64575, 64590',
    impact: 'medium',
    marketImpact: 'Improvement from previous negative policy. Affects approximately 3.8M lives in North Carolina.',
    recommendations: ['Develop provider education on documentation requirements', 'Monitor implementation and appeal success rates']
  }, {
    id: 18,
    type: 'Active',
    status: 'negative',
    payer: 'Capital BlueCross',
    insuredLives: '1,234,567',
    title: 'Peripheral Subcutaneous Field Stimulation (PSFS)',
    policyNumber: 'MP 7.043',
    updated: '02/15/2025',
    effectiveDate: '04/01/2025',
    reviewDate: '02/15/2026',
    previousStatus: 'negative',
    changeHighlights: ['Annual review with no substantive changes', 'Maintained investigational classification'],
    coverageStatement: 'Peripheral subcutaneous field stimulation is considered investigational for all indications, including but not limited to treatment of chronic pain.',
    codes: '64555, 64999',
    impact: 'low',
    marketImpact: 'Continued negative coverage affecting approximately 1.2M lives in central Pennsylvania.',
    recommendations: ['Low priority for engagement based on market size', 'Monitor for policy updates']
  }, {
    id: 19,
    type: 'Active',
    status: 'positive',
    payer: 'Highmark',
    insuredLives: '5,678,901',
    title: 'Peripheral Nerve Stimulation',
    policyNumber: 'S-230',
    updated: '02/01/2025',
    effectiveDate: '03/15/2025',
    reviewDate: '02/01/2026',
    previousStatus: 'restricted',
    changeHighlights: ['Expanded coverage criteria', 'Removed several restrictions from previous policy', 'Added coverage for additional nerve applications'],
    coverageStatement: 'Peripheral nerve stimulation may be considered medically necessary for the treatment of chronic pain when specific criteria are met.',
    codes: '64555, 64575, 64590, 64595',
    impact: 'medium',
    marketImpact: 'Positive policy affecting approximately 5.7M lives across multiple states, primarily in the Northeast.',
    recommendations: ['Develop provider education program', 'Create case studies highlighting successful patient outcomes']
  }, {
    id: 20,
    type: 'Active',
    status: 'no-policy',
    payer: 'Premera Blue Cross',
    insuredLives: '2,345,678',
    title: '',
    policyNumber: '',
    updated: '',
    effectiveDate: '',
    reviewDate: '',
    previousStatus: 'no-policy',
    changeHighlights: [],
    coverageStatement: '',
    codes: '',
    impact: 'medium',
    marketImpact: 'Opportunity to establish coverage through medical necessity determinations. Affects approximately 2.3M lives in Pacific Northwest.',
    recommendations: ['Develop medical director engagement strategy', 'Prepare case-by-case review support materials']
  }, {
    id: 21,
    type: 'Active',
    status: 'restricted',
    payer: 'Premera Blue Cross',
    insuredLives: '2,345,678',
    title: 'Peripheral Nerve Stimulation',
    policyNumber: 'SUR.00134',
    updated: '01/05/2025',
    effectiveDate: '03/01/2025',
    reviewDate: '01/05/2026',
    previousStatus: 'negative',
    changeHighlights: ['Added coverage for specific indications with restrictions', 'Requires extensive documentation and prior authorization'],
    coverageStatement: 'Peripheral nerve stimulation may be considered medically necessary for select patients with specific nerve injuries when criteria are met.',
    codes: '64555, 64575, 64590',
    impact: 'medium',
    marketImpact: 'Improvement from previous negative policy. Affects approximately 2.3M lives in Pacific Northwest.',
    recommendations: ['Develop provider education on documentation requirements', 'Monitor implementation and appeal success rates']
  }, {
    id: 22,
    type: 'Active',
    status: 'negative',
    payer: 'Novitas Solutions (CMS)',
    insuredLives: '15,678,901',
    title: 'Billing and Coding: Auricular Peripheral Nerve Stimulation (Electro-Acupuncture Device)',
    policyNumber: 'A56923',
    updated: '09/01/2024',
    effectiveDate: '11/01/2024',
    reviewDate: '09/01/2025',
    previousStatus: 'negative',
    changeHighlights: ['Annual review with no substantive changes', 'Maintained non-coverage position'],
    coverageStatement: 'Auricular peripheral nerve field stimulation is not reasonable and necessary for the treatment of acute or chronic pain. This procedure is considered investigational.',
    codes: '0451T, 0452T, 0453T, 0454T',
    impact: 'high',
    marketImpact: 'Medicare contractor determination affecting large Medicare population across multiple states.',
    recommendations: ['Focus on commercial payers rather than Medicare', 'Monitor for potential LCD reconsideration']
  }, {
    id: 23,
    type: 'Active',
    status: 'negative',
    payer: 'Harvard Pilgrim',
    insuredLives: '3,456,789',
    title: 'Peripheral Nerve Stimulation for Pain',
    policyNumber: 'MP-023',
    updated: '04/01/2025',
    effectiveDate: '06/01/2025',
    reviewDate: '04/01/2026',
    previousStatus: 'negative',
    changeHighlights: ['Updated references but no substantive changes', 'Maintained investigational classification'],
    coverageStatement: 'Peripheral nerve stimulation is considered investigational for the treatment of pain.',
    codes: '64555, 64575, 64590, 64595',
    impact: 'medium',
    marketImpact: 'Continued negative coverage affecting approximately 3.5M lives in New England region.',
    recommendations: ['Submit updated clinical evidence package', 'Engage with medical directors']
  }, {
    id: 24,
    type: 'Active',
    status: 'positive',
    payer: 'Priority Health',
    insuredLives: '1,234,567',
    title: 'Peripheral Nerve Stimulation for Chronic Pain',
    policyNumber: '91612',
    updated: '03/15/2025',
    effectiveDate: '05/01/2025',
    reviewDate: '03/15/2026',
    previousStatus: 'restricted',
    changeHighlights: ['Expanded coverage criteria', 'Removed requirement for trial period', 'Added coverage for additional nerve applications'],
    coverageStatement: 'Peripheral nerve stimulation is considered medically necessary for the treatment of chronic pain when specific criteria are met.',
    codes: '64555, 64575, 64590, 64595',
    impact: 'low',
    marketImpact: 'Positive policy but limited impact due to smaller membership base in Michigan.',
    recommendations: ['Develop provider education program', 'Use as model policy for other regional payers']
  }, {
    id: 25,
    type: 'Active',
    status: 'no-policy',
    payer: 'Blue Shield of California',
    insuredLives: '4,567,890',
    title: '',
    policyNumber: '',
    updated: '',
    effectiveDate: '',
    reviewDate: '',
    previousStatus: 'no-policy',
    changeHighlights: [],
    coverageStatement: '',
    codes: '',
    impact: 'high',
    marketImpact: 'Significant opportunity in major California market. Case-by-case determinations likely.',
    recommendations: ['Develop medical director engagement strategy', 'Prepare case-by-case review support materials', 'Monitor for policy development']
  }, {
    id: 26,
    type: 'Active',
    status: 'restricted',
    payer: 'Independence Blue Cross',
    insuredLives: '2,789,012',
    title: 'Peripheral Nerve Stimulation',
    policyNumber: '08.01.01g',
    updated: '02/15/2025',
    effectiveDate: '04/01/2025',
    reviewDate: '02/15/2026',
    previousStatus: 'negative',
    changeHighlights: ['New restricted coverage policy', 'Limited to specific nerve applications and diagnoses', 'Requires extensive documentation'],
    coverageStatement: 'Peripheral nerve stimulation may be considered medically necessary for select patients with chronic, intractable pain when specific criteria are met.',
    codes: '64555, 64575, 64590',
    impact: 'medium',
    marketImpact: 'Improvement from previous negative policy. Affects approximately 2.8M lives in Philadelphia region.',
    recommendations: ['Develop provider education on documentation requirements', 'Monitor implementation and appeal success rates']
  }, {
    id: 27,
    type: 'Active',
    status: 'negative',
    payer: 'BCBS Florida (Florida Blue)',
    insuredLives: '5,678,901',
    title: 'Peripheral Nerve Stimulation',
    policyNumber: '08.01.01',
    updated: '05/01/2025',
    effectiveDate: '07/01/2025',
    reviewDate: '05/01/2026',
    previousStatus: 'negative',
    changeHighlights: ['Updated references but no substantive changes', 'Maintained investigational classification'],
    coverageStatement: 'Peripheral nerve stimulation is considered investigational for the treatment of pain.',
    codes: '64555, 64575, 64590, 64595',
    impact: 'high',
    marketImpact: 'Continued negative coverage affecting approximately 5.7M lives in Florida.',
    recommendations: ['Submit updated clinical evidence package', 'Engage with medical directors']
  }, {
    id: 28,
    type: 'Active',
    status: 'positive',
    payer: 'BCBS Minnesota',
    insuredLives: '2,890,123',
    title: 'Peripheral Nerve Stimulation for Chronic Pain',
    policyNumber: 'MED007',
    updated: '04/15/2025',
    effectiveDate: '06/01/2025',
    reviewDate: '04/15/2026',
    previousStatus: 'restricted',
    changeHighlights: ['Expanded coverage criteria', 'Removed several restrictions from previous policy', 'Added coverage for additional nerve applications'],
    coverageStatement: 'Peripheral nerve stimulation may be considered medically necessary for the treatment of chronic pain when specific criteria are met.',
    codes: '64555, 64575, 64590, 64595',
    impact: 'medium',
    marketImpact: 'Positive policy affecting approximately 2.9M lives in Minnesota.',
    recommendations: ['Develop provider education program', 'Create case studies highlighting successful patient outcomes']
  }, {
    id: 29,
    type: 'Active',
    status: 'negative',
    payer: 'MVP Health Care',
    insuredLives: '1,234,567',
    title: 'Peripheral Nerve Stimulation',
    policyNumber: 'MP 6.062',
    updated: '03/01/2025',
    effectiveDate: '05/01/2025',
    reviewDate: '03/01/2026',
    previousStatus: 'negative',
    changeHighlights: ['Annual review with no substantive changes', 'Maintained investigational classification'],
    coverageStatement: 'Peripheral nerve stimulation is considered investigational for the treatment of pain.',
    codes: '64555, 64575, 64590, 64595',
    impact: 'low',
    marketImpact: 'Continued negative coverage affecting approximately 1.2M lives in New York and Vermont.',
    recommendations: ['Low priority for engagement based on market size', 'Monitor for policy updates']
  }, {
    id: 30,
    type: 'Active',
    status: 'no-policy',
    payer: 'Horizon BCBS New Jersey',
    insuredLives: '3,456,789',
    title: '',
    policyNumber: '',
    updated: '',
    effectiveDate: '',
    reviewDate: '',
    previousStatus: 'no-policy',
    changeHighlights: [],
    coverageStatement: '',
    codes: '',
    impact: 'medium',
    marketImpact: 'Opportunity to establish coverage through medical necessity determinations. Affects approximately 3.5M lives in New Jersey.',
    recommendations: ['Develop medical director engagement strategy', 'Prepare case-by-case review support materials']
  }];
  // Calculate stats
  const totalLives = policies.reduce((sum, policy) => {
    return sum + parseInt(policy.insuredLives.replace(/,/g, ''), 10);
  }, 0);
  const negativeLives = policies.filter(p => p.status === 'negative').reduce((sum, policy) => sum + parseInt(policy.insuredLives.replace(/,/g, ''), 10), 0);
  const positiveLives = policies.filter(p => p.status === 'positive').reduce((sum, policy) => sum + parseInt(policy.insuredLives.replace(/,/g, ''), 10), 0);
  return <div className="px-4 py-6 md:px-6 lg:px-8 bg-slate-50" data-id="element-1080">
      <div className="mb-6" data-id="element-1081">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between" data-id="element-1082">
          <div data-id="element-1083">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-1084">
              Policy Analysis
            </h1>
            <p className="mt-1 text-sm text-gray-500" data-id="element-1085">
              Deep analysis of policy changes and their impact on coverage
            </p>
          </div>
          <div className="mt-4 md:mt-0 flex items-center space-x-3" data-id="element-1086">
            <div className="p-1 bg-white rounded-lg shadow flex" data-id="element-1087">
              <button className={`p-2 ${viewMode === 'card' ? 'bg-blue-100 text-blue-700' : 'text-gray-500'} rounded`} onClick={() => setViewMode('card')} title="Card View" data-id="element-1088">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1089">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" data-id="element-1090" />
                </svg>
              </button>
              <button className={`p-2 ${viewMode === 'table' ? 'bg-blue-100 text-blue-700' : 'text-gray-500'} rounded`} onClick={() => setViewMode('table')} title="Table View" data-id="element-1091">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1092">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" data-id="element-1093" />
                </svg>
              </button>
            </div>
            <button className="px-3 py-2 text-sm font-medium rounded-md bg-white text-gray-700 border border-gray-300 shadow flex items-center" onClick={() => setShowExportModal(true)} data-id="element-1094">
              <DownloadIcon className="w-4 h-4 mr-2" data-id="element-1095" />
              Export
            </button>
            <button className={`px-3 py-2 text-sm font-medium rounded-md ${showComparisonView ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300'} shadow`} onClick={() => setShowComparisonView(!showComparisonView)} data-id="element-1096">
              {showComparisonView ? 'Exit Comparison' : 'Compare Policies'}
            </button>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mb-6" data-id="element-1097">
        <div className="p-4 bg-white rounded-lg shadow flex items-center" data-id="element-1098">
          <div className="p-3 rounded-full bg-blue-100 mr-4" data-id="element-1099">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1100">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" data-id="element-1101" />
            </svg>
          </div>
          <div data-id="element-1102">
            <p className="text-sm text-gray-500" data-id="element-1103">Total Insured Lives</p>
            <p className="text-xl font-semibold" data-id="element-1104">
              {(totalLives / 1000000).toFixed(1)}M
            </p>
          </div>
        </div>
        <div className="p-4 bg-white rounded-lg shadow flex items-center" data-id="element-1105">
          <div className="p-3 rounded-full bg-red-100 mr-4" data-id="element-1106">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1107">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" data-id="element-1108" />
            </svg>
          </div>
          <div data-id="element-1109">
            <p className="text-sm text-gray-500" data-id="element-1110">Negative Coverage</p>
            <p className="text-xl font-semibold" data-id="element-1111">
              {(negativeLives / 1000000).toFixed(1)}M
            </p>
            <p className="text-xs text-gray-500" data-id="element-1112">
              {(negativeLives / totalLives * 100).toFixed(1)}% of total
            </p>
          </div>
        </div>
        <div className="p-4 bg-white rounded-lg shadow flex items-center" data-id="element-1113">
          <div className="p-3 rounded-full bg-green-100 mr-4" data-id="element-1114">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1115">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-1116" />
            </svg>
          </div>
          <div data-id="element-1117">
            <p className="text-sm text-gray-500" data-id="element-1118">Positive Coverage</p>
            <p className="text-xl font-semibold" data-id="element-1119">
              {(positiveLives / 1000000).toFixed(1)}M
            </p>
            <p className="text-xs text-gray-500" data-id="element-1120">
              {(positiveLives / totalLives * 100).toFixed(1)}% of total
            </p>
          </div>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex mb-6 border-b border-gray-200" data-id="element-1121">
        <button className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === 'all' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('all')} data-id="element-1122">
          All Policies
        </button>
        <button className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === 'negative' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('negative')} data-id="element-1123">
          Negative Coverage
        </button>
        <button className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === 'positive' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('positive')} data-id="element-1124">
          Positive Coverage
        </button>
        <button className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === 'changes' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('changes')} data-id="element-1125">
          Recent Changes
        </button>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col items-stretch mb-6 space-y-4 md:flex-row md:items-center md:space-y-0 md:space-x-4" data-id="element-1126">
        <div className="relative flex-grow" data-id="element-1127">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-1128">
            <SearchIcon className="w-5 h-5 text-gray-400" data-id="element-1129" />
          </div>
          <input type="text" className="block w-full py-2 pl-10 pr-3 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder="Search policies by title, payer, or code..." data-id="element-1130" />
        </div>
        <div className="flex space-x-4" data-id="element-1131">
          <button className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50" onClick={() => setShowAdvancedFilters(true)} data-id="element-1132">
            <FilterIcon className="w-4 h-4 mr-2 text-gray-500" data-id="element-1133" />
            Advanced Filters
          </button>
          <select className="px-4 py-2 text-sm border border-gray-300 rounded-md bg-white shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500" data-id="element-1134">
            <option data-id="element-1135">Sort by: Impact</option>
            <option data-id="element-1136">Sort by: Date</option>
            <option data-id="element-1137">Sort by: Lives</option>
            <option data-id="element-1138">Sort by: Payer</option>
          </select>
        </div>
      </div>

      {/* Policy cards or table */}
      {viewMode === 'card' && <div className="space-y-6" data-id="element-1139">
          {policies.filter(policy => activeTab === 'all' || activeTab === 'negative' && policy.status === 'negative' || activeTab === 'positive' && policy.status === 'positive' || activeTab === 'changes' && policy.previousStatus !== policy.status).map(policy => <div key={policy.id} className="overflow-hidden bg-white rounded-lg shadow" data-id="element-1140">
                {/* Policy header - always visible */}
                <div className={`border-l-4 ${policy.status === 'negative' ? 'border-red-500' : policy.status === 'positive' ? 'border-green-500' : policy.status === 'no-policy' ? 'border-yellow-500' : 'border-slate-400'}`} data-id="element-1141">
                  <div className="flex flex-col p-4 cursor-pointer md:flex-row md:items-center md:justify-between" onClick={() => toggleExpand(policy.id)} data-id="element-1142">
                    <div className="flex items-start mb-3 md:mb-0" data-id="element-1143">
                      <StatusBadge status={policy.status as any} data-id="element-1144" />
                      <div className="ml-3" data-id="element-1145">
                        <div className="flex items-center" data-id="element-1146">
                          <h3 className="text-lg font-medium text-gray-900" data-id="element-1147">
                            {policy.title || 'No Policy Available'}
                          </h3>
                          {policy.previousStatus !== policy.status && <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full" data-id="element-1148">
                              Changed
                            </span>}
                        </div>
                        <p className="text-sm text-gray-600" data-id="element-1149">{policy.payer}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between md:space-x-8" data-id="element-1150">
                      <div className="text-right" data-id="element-1151">
                        <p className="text-sm text-gray-500" data-id="element-1152">Insured Lives</p>
                        <p className="font-medium" data-id="element-1153">{policy.insuredLives}</p>
                      </div>
                      {policy.impact && <div className="text-right" data-id="element-1154">
                          <p className="text-sm text-gray-500" data-id="element-1155">Impact</p>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${policy.impact === 'high' ? 'bg-red-100 text-red-800' : policy.impact === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-1156">
                            {policy.impact.charAt(0).toUpperCase() + policy.impact.slice(1)}
                          </span>
                        </div>}
                      <div className="flex items-center justify-center w-8 h-8 ml-4 rounded-full hover:bg-gray-100" data-id="element-1157">
                        {expandedPolicy === policy.id ? <ChevronUpIcon className="w-5 h-5 text-gray-500" data-id="element-1158" /> : <ChevronDownIcon className="w-5 h-5 text-gray-500" data-id="element-1159" />}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Expanded policy details */}
                {expandedPolicy === policy.id && <div className="px-4 py-5 border-t border-gray-200" data-id="element-1160">
                    {policy.status !== 'no-policy' ? <>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 mb-6" data-id="element-1161">
                          {policy.policyNumber && <div data-id="element-1162">
                              <p className="text-sm font-medium text-gray-500" data-id="element-1163">
                                Policy Number
                              </p>
                              <p className="mt-1" data-id="element-1164">{policy.policyNumber}</p>
                            </div>}
                          {policy.effectiveDate && <div data-id="element-1165">
                              <p className="text-sm font-medium text-gray-500" data-id="element-1166">
                                Effective Date
                              </p>
                              <p className="mt-1" data-id="element-1167">{policy.effectiveDate}</p>
                            </div>}
                          {policy.reviewDate && <div data-id="element-1168">
                              <p className="text-sm font-medium text-gray-500" data-id="element-1169">
                                Next Review
                              </p>
                              <p className="mt-1" data-id="element-1170">{policy.reviewDate}</p>
                            </div>}
                        </div>
                        {policy.previousStatus !== policy.status && <div className="mb-6 p-4 bg-blue-50 rounded-lg" data-id="element-1171">
                            <h4 className="text-sm font-medium text-blue-800 mb-2" data-id="element-1172">
                              Policy Change Details
                            </h4>
                            <div className="flex items-center mb-3" data-id="element-1173">
                              <div className="flex items-center" data-id="element-1174">
                                <p className="text-sm text-gray-700" data-id="element-1175">
                                  Previous Status:
                                </p>
                                <div className="ml-2" data-id="element-1176">
                                  <StatusBadge status={policy.previousStatus as any} size="sm" data-id="element-1177" />
                                </div>
                              </div>
                              <div className="mx-3 text-gray-400" data-id="element-1178">→</div>
                              <div className="flex items-center" data-id="element-1179">
                                <p className="text-sm text-gray-700" data-id="element-1180">
                                  Current Status:
                                </p>
                                <div className="ml-2" data-id="element-1181">
                                  <StatusBadge status={policy.status as any} size="sm" data-id="element-1182" />
                                </div>
                              </div>
                            </div>
                            {policy.changeHighlights && policy.changeHighlights.length > 0 && <div data-id="element-1183">
                                  <h5 className="text-sm font-medium text-gray-700 mb-1" data-id="element-1184">
                                    Key Changes:
                                  </h5>
                                  <ul className="pl-5 list-disc text-sm text-gray-700 space-y-1" data-id="element-1185">
                                    {policy.changeHighlights.map((change, index) => <li key={index} data-id="element-1186">{change}</li>)}
                                  </ul>
                                </div>}
                          </div>}
                        <div className="mb-6" data-id="element-1187">
                          <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1188">
                            Coverage Statement
                          </h4>
                          <div className="p-4 bg-gray-50 rounded-lg" data-id="element-1189">
                            <p className="text-sm text-gray-700" data-id="element-1190">
                              {policy.coverageStatement}
                            </p>
                          </div>
                        </div>
                        {policy.codes && <div className="mb-6" data-id="element-1191">
                            <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1192">
                              CPT/HCPCS Codes
                            </h4>
                            <p className="text-sm text-gray-700" data-id="element-1193">
                              {policy.codes}
                            </p>
                          </div>}
                        {policy.marketImpact && <div className="mb-6" data-id="element-1194">
                            <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1195">
                              Market Impact Analysis
                            </h4>
                            <div className="p-4 bg-gray-50 rounded-lg" data-id="element-1196">
                              <p className="text-sm text-gray-700" data-id="element-1197">
                                {policy.marketImpact}
                              </p>
                            </div>
                          </div>}
                        {policy.recommendations && policy.recommendations.length > 0 && <div className="mb-6" data-id="element-1198">
                              <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1199">
                                Recommended Actions
                              </h4>
                              <ul className="pl-5 list-disc text-sm text-gray-700 space-y-1" data-id="element-1200">
                                {policy.recommendations.map((rec, index) => <li key={index} data-id="element-1201">{rec}</li>)}
                              </ul>
                            </div>}
                      </> : <div className="text-center py-6" data-id="element-1202">
                        <div className="mx-auto h-12 w-12 text-gray-400" data-id="element-1203">
                          <FileTextIcon className="h-12 w-12" data-id="element-1204" />
                        </div>
                        <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-1205">
                          No Policy Available
                        </h3>
                        <p className="mt-1 text-sm text-gray-500" data-id="element-1206">
                          This payer does not have a specific policy for this
                          product/service.
                        </p>
                        {policy.marketImpact && <div className="mt-4 p-4 bg-gray-50 rounded-lg text-left" data-id="element-1207">
                            <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1208">
                              Market Impact Analysis
                            </h4>
                            <p className="text-sm text-gray-700" data-id="element-1209">
                              {policy.marketImpact}
                            </p>
                          </div>}
                        {policy.recommendations && policy.recommendations.length > 0 && <div className="mt-4 text-left" data-id="element-1210">
                              <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1211">
                                Recommended Actions
                              </h4>
                              <ul className="pl-5 list-disc text-sm text-gray-700 space-y-1" data-id="element-1212">
                                {policy.recommendations.map((rec, index) => <li key={index} data-id="element-1213">{rec}</li>)}
                              </ul>
                            </div>}
                      </div>}
                    <div className="flex justify-end mt-6 space-x-3" data-id="element-1214">
                      <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 flex items-center" data-id="element-1215">
                        <DownloadIcon className="w-4 h-4 mr-2" data-id="element-1216" />
                        Export
                      </button>
                      {policy.status !== 'no-policy' && <button className="px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100 flex items-center" data-id="element-1217">
                          <ExternalLinkIcon className="w-4 h-4 mr-2" data-id="element-1218" />
                          View Full Policy
                        </button>}
                      <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" data-id="element-1219">
                        Impact Analysis
                      </button>
                    </div>
                  </div>}
              </div>)}
        </div>}

      {viewMode === 'table' && <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-1220">
          <div className="overflow-x-auto" data-id="element-1221">
            <table className="min-w-full divide-y divide-gray-200" data-id="element-1222">
              <thead className="bg-gray-50" data-id="element-1223">
                <tr data-id="element-1224">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1225">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1226">
                    Payer
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1227">
                    Policy Title
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1228">
                    Insured Lives
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1229">
                    Impact
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1230">
                    Effective Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1231">
                    Next Review
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1232">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-1233">
                {policies.filter(policy => activeTab === 'all' || activeTab === 'negative' && policy.status === 'negative' || activeTab === 'positive' && policy.status === 'positive' || activeTab === 'changes' && policy.previousStatus !== policy.status).map(policy => <tr key={policy.id} className="hover:bg-gray-50" data-id="element-1234">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1235">
                        <StatusBadge status={policy.status as any} data-id="element-1236" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1237">
                        <div className="text-sm font-medium text-blue-600" data-id="element-1238">
                          {policy.payer}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-1239">
                        <div className="text-sm text-gray-900" data-id="element-1240">
                          {policy.title || 'No Policy Available'}
                        </div>
                        {policy.policyNumber && <div className="text-xs text-gray-500" data-id="element-1241">
                            #{policy.policyNumber}
                          </div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1242">
                        {policy.insuredLives}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-1243">
                        {policy.impact && <span className={`px-2 py-1 text-xs font-medium rounded-full ${policy.impact === 'high' ? 'bg-red-100 text-red-800' : policy.impact === 'medium' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`} data-id="element-1244">
                            {policy.impact.charAt(0).toUpperCase() + policy.impact.slice(1)}
                          </span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1245">
                        {policy.effectiveDate || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1246">
                        {policy.reviewDate || '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-1247">
                        <button className="text-blue-600 hover:text-blue-900 mr-3" data-id="element-1248">
                          View
                        </button>
                        <button className="text-gray-600 hover:text-gray-900" data-id="element-1249">
                          Export
                        </button>
                      </td>
                    </tr>)}
              </tbody>
            </table>
          </div>
        </div>}

      {/* Advanced Filters Modal */}
      {showAdvancedFilters && <div className="fixed inset-0 z-10 overflow-y-auto" data-id="element-1250">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-1251">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-1252">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setShowAdvancedFilters(false)} data-id="element-1253"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-1254">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full" data-id="element-1255">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-1256">
                <div className="sm:flex sm:items-start" data-id="element-1257">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full" data-id="element-1258">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title" data-id="element-1259">
                      Advanced Filters
                    </h3>
                    <div className="mt-4" data-id="element-1260">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1261">
                        <div data-id="element-1262">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1263">
                            Coverage Status
                          </label>
                          <div className="space-y-2" data-id="element-1264">
                            <div className="flex items-center" data-id="element-1265">
                              <input id="status-positive" name="status-positive" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1266" />
                              <label htmlFor="status-positive" className="ml-2 text-sm text-gray-700" data-id="element-1267">
                                Positive
                              </label>
                            </div>
                            <div className="flex items-center" data-id="element-1268">
                              <input id="status-negative" name="status-negative" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1269" />
                              <label htmlFor="status-negative" className="ml-2 text-sm text-gray-700" data-id="element-1270">
                                Negative
                              </label>
                            </div>
                            <div className="flex items-center" data-id="element-1271">
                              <input id="status-restricted" name="status-restricted" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1272" />
                              <label htmlFor="status-restricted" className="ml-2 text-sm text-gray-700" data-id="element-1273">
                                Restricted
                              </label>
                            </div>
                            <div className="flex items-center" data-id="element-1274">
                              <input id="status-no-policy" name="status-no-policy" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1275" />
                              <label htmlFor="status-no-policy" className="ml-2 text-sm text-gray-700" data-id="element-1276">
                                No Policy
                              </label>
                            </div>
                          </div>
                        </div>
                        <div data-id="element-1277">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1278">
                            Impact Level
                          </label>
                          <div className="space-y-2" data-id="element-1279">
                            <div className="flex items-center" data-id="element-1280">
                              <input id="impact-high" name="impact-high" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1281" />
                              <label htmlFor="impact-high" className="ml-2 text-sm text-gray-700" data-id="element-1282">
                                High Impact
                              </label>
                            </div>
                            <div className="flex items-center" data-id="element-1283">
                              <input id="impact-medium" name="impact-medium" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1284" />
                              <label htmlFor="impact-medium" className="ml-2 text-sm text-gray-700" data-id="element-1285">
                                Medium Impact
                              </label>
                            </div>
                            <div className="flex items-center" data-id="element-1286">
                              <input id="impact-low" name="impact-low" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1287" />
                              <label htmlFor="impact-low" className="ml-2 text-sm text-gray-700" data-id="element-1288">
                                Low Impact
                              </label>
                            </div>
                          </div>
                        </div>
                        <div data-id="element-1289">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1290">
                            Payer Type
                          </label>
                          <div className="space-y-2" data-id="element-1291">
                            <div className="flex items-center" data-id="element-1292">
                              <input id="payer-bcbs" name="payer-bcbs" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1293" />
                              <label htmlFor="payer-bcbs" className="ml-2 text-sm text-gray-700" data-id="element-1294">
                                BCBS
                              </label>
                            </div>
                            <div className="flex items-center" data-id="element-1295">
                              <input id="payer-commercial" name="payer-commercial" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1296" />
                              <label htmlFor="payer-commercial" className="ml-2 text-sm text-gray-700" data-id="element-1297">
                                Commercial
                              </label>
                            </div>
                            <div className="flex items-center" data-id="element-1298">
                              <input id="payer-medicare" name="payer-medicare" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1299" />
                              <label htmlFor="payer-medicare" className="ml-2 text-sm text-gray-700" data-id="element-1300">
                                Medicare
                              </label>
                            </div>
                            <div className="flex items-center" data-id="element-1301">
                              <input id="payer-medicaid" name="payer-medicaid" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1302" />
                              <label htmlFor="payer-medicaid" className="ml-2 text-sm text-gray-700" data-id="element-1303">
                                Medicaid
                              </label>
                            </div>
                          </div>
                        </div>
                        <div data-id="element-1304">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1305">
                            Policy Changes
                          </label>
                          <div className="space-y-2" data-id="element-1306">
                            <div className="flex items-center" data-id="element-1307">
                              <input id="changes-any" name="changes-any" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1308" />
                              <label htmlFor="changes-any" className="ml-2 text-sm text-gray-700" data-id="element-1309">
                                Any Changes
                              </label>
                            </div>
                            <div className="flex items-center" data-id="element-1310">
                              <input id="changes-positive" name="changes-positive" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1311" />
                              <label htmlFor="changes-positive" className="ml-2 text-sm text-gray-700" data-id="element-1312">
                                Positive Changes
                              </label>
                            </div>
                            <div className="flex items-center" data-id="element-1313">
                              <input id="changes-negative" name="changes-negative" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-1314" />
                              <label htmlFor="changes-negative" className="ml-2 text-sm text-gray-700" data-id="element-1315">
                                Negative Changes
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-6" data-id="element-1316">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1317">
                          Date Range
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1318">
                          <div data-id="element-1319">
                            <label className="block text-xs text-gray-500 mb-1" data-id="element-1320">
                              From
                            </label>
                            <input type="date" className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-1321" />
                          </div>
                          <div data-id="element-1322">
                            <label className="block text-xs text-gray-500 mb-1" data-id="element-1323">
                              To
                            </label>
                            <input type="date" className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-1324" />
                          </div>
                        </div>
                      </div>
                      <div className="mt-6" data-id="element-1325">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1326">
                          CPT/HCPCS Codes
                        </label>
                        <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Enter codes separated by commas (e.g., 64555, 64590)" data-id="element-1327" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-1328">
                <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-1329">
                  Apply Filters
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => setShowAdvancedFilters(false)} data-id="element-1330">
                  Cancel
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm" data-id="element-1331">
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Export Modal */}
      {showExportModal && <div className="fixed inset-0 z-10 overflow-y-auto" data-id="element-1332">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-1333">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-1334">
              <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setShowExportModal(false)} data-id="element-1335"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-1336">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-1337">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-1338">
                <div className="sm:flex sm:items-start" data-id="element-1339">
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full" data-id="element-1340">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title" data-id="element-1341">
                      Export Policy Analysis
                    </h3>
                    <div className="mt-4" data-id="element-1342">
                      <div className="mb-4" data-id="element-1343">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1344">
                          Export Format
                        </label>
                        <select className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" data-id="element-1345">
                          <option data-id="element-1346">PDF Report</option>
                          <option data-id="element-1347">Excel Spreadsheet</option>
                          <option data-id="element-1348">CSV File</option>
                          <option data-id="element-1349">PowerPoint Presentation</option>
                        </select>
                      </div>
                      <div className="mb-4" data-id="element-1350">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1351">
                          Content to Include
                        </label>
                        <div className="mt-2 space-y-2" data-id="element-1352">
                          <div className="flex items-center" data-id="element-1353">
                            <input id="content-current" name="content" type="radio" className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300" checked data-id="element-1354" />
                            <label htmlFor="content-current" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-1355">
                              Current View Only
                            </label>
                          </div>
                          <div className="flex items-center" data-id="element-1356">
                            <input id="content-all" name="content" type="radio" className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300" data-id="element-1357" />
                            <label htmlFor="content-all" className="ml-3 block text-sm font-medium text-gray-700" data-id="element-1358">
                              All Policies
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="mb-4" data-id="element-1359">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1360">
                          Include Details
                        </label>
                        <div className="mt-2 space-y-2" data-id="element-1361">
                          <div className="flex items-center" data-id="element-1362">
                            <input id="include-summary" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" checked data-id="element-1363" />
                            <label htmlFor="include-summary" className="ml-2 text-sm text-gray-700" data-id="element-1364">
                              Summary Statistics
                            </label>
                          </div>
                          <div className="flex items-center" data-id="element-1365">
                            <input id="include-policy-details" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" checked data-id="element-1366" />
                            <label htmlFor="include-policy-details" className="ml-2 text-sm text-gray-700" data-id="element-1367">
                              Policy Details
                            </label>
                          </div>
                          <div className="flex items-center" data-id="element-1368">
                            <input id="include-market-impact" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" checked data-id="element-1369" />
                            <label htmlFor="include-market-impact" className="ml-2 text-sm text-gray-700" data-id="element-1370">
                              Market Impact Analysis
                            </label>
                          </div>
                          <div className="flex items-center" data-id="element-1371">
                            <input id="include-recommendations" type="checkbox" className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" checked data-id="element-1372" />
                            <label htmlFor="include-recommendations" className="ml-2 text-sm text-gray-700" data-id="element-1373">
                              Recommendations
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-1374">
                <button type="button" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-1375">
                  Export
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => setShowExportModal(false)} data-id="element-1376">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* Comparison View */}
      {showComparisonView && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50" data-id="element-1377">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" data-id="element-1378">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-1379">
              <h3 className="text-lg font-medium text-gray-900" data-id="element-1380">
                Policy Comparison
              </h3>
              <button className="text-gray-400 hover:text-gray-500" onClick={() => setShowComparisonView(false)} data-id="element-1381">
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-1382">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" data-id="element-1383" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-auto flex-1" data-id="element-1384">
              <div className="mb-6 flex items-center space-x-4" data-id="element-1385">
                <div data-id="element-1386">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1387">
                    Select Payers to Compare
                  </label>
                  <div className="flex space-x-2" data-id="element-1388">
                    <select className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm" value={selectedPayer || ''} onChange={e => setSelectedPayer(e.target.value)} data-id="element-1389">
                      <option value="" data-id="element-1390">Select a payer</option>
                      {policies.map(policy => <option key={policy.id} value={policy.payer} data-id="element-1391">
                          {policy.payer}
                        </option>)}
                    </select>
                    <button className="px-3 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700" data-id="element-1392">
                      Add to Comparison
                    </button>
                  </div>
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-1393">
                <table className="min-w-full divide-y divide-gray-200" data-id="element-1394">
                  <thead className="bg-gray-50" data-id="element-1395">
                    <tr data-id="element-1396">
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4" data-id="element-1397">
                        Comparison Criteria
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1398">
                        Aetna
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1399">
                        BCBS Alabama
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-1400">
                    <tr data-id="element-1401">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 bg-gray-50" data-id="element-1402">
                        Coverage Status
                      </td>
                      <td className="px-6 py-4" data-id="element-1403">
                        <StatusBadge status="negative" data-id="element-1404" />
                      </td>
                      <td className="px-6 py-4" data-id="element-1405">
                        <StatusBadge status="positive" data-id="element-1406" />
                      </td>
                    </tr>
                    <tr data-id="element-1407">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 bg-gray-50" data-id="element-1408">
                        Insured Lives
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900" data-id="element-1409">
                        25,287,393
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900" data-id="element-1410">
                        2,533,935
                      </td>
                    </tr>
                    <tr data-id="element-1411">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 bg-gray-50" data-id="element-1412">
                        Policy Title
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900" data-id="element-1413">
                        Electrical Stimulation for Pain
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900" data-id="element-1414">
                        Peripheral Nerve Stimulation of the Trunk or Limbs for
                        Treatment of Pain
                      </td>
                    </tr>
                    <tr data-id="element-1415">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 bg-gray-50" data-id="element-1416">
                        Effective Date
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900" data-id="element-1417">
                        04/14/2025
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900" data-id="element-1418">
                        10/01/2024
                      </td>
                    </tr>
                    <tr data-id="element-1419">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 bg-gray-50" data-id="element-1420">
                        Next Review
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900" data-id="element-1421">
                        01/08/2026
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900" data-id="element-1422">
                        10/01/2025
                      </td>
                    </tr>
                    <tr data-id="element-1423">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 bg-gray-50" data-id="element-1424">
                        Coverage Statement
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900" data-id="element-1425">
                        <div className="max-h-24 overflow-y-auto" data-id="element-1426">
                          The following durable medical equipment (DME) and
                          supplies are considered experimental, investigational,
                          or unproven because the effectiveness of these
                          approaches for the specified indication(s) has not
                          been established (not an all-inclusive list):
                          Peripheral subcutaneous field stimulation (PSFS) or
                          peripheral nerve field stimulation (PNFS)...
                        </div>
                        <button className="mt-2 text-xs text-blue-600 hover:text-blue-800" data-id="element-1427">
                          View Full Statement
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900" data-id="element-1428">
                        <div className="max-h-24 overflow-y-auto" data-id="element-1429">
                          Peripheral nerve stimulation of the trunk or limbs may
                          be considered medically necessary for the treatment of
                          chronic intractable pain that is refractory to all
                          other pain therapies, when ALL of the following
                          criteria, clearly documented in the individuals
                          record, are met...
                        </div>
                        <button className="mt-2 text-xs text-blue-600 hover:text-blue-800" data-id="element-1430">
                          View Full Statement
                        </button>
                      </td>
                    </tr>
                    <tr data-id="element-1431">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 bg-gray-50" data-id="element-1432">
                        CPT/HCPCS Codes
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900" data-id="element-1433">
                        64555 listed in policy.
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900" data-id="element-1434">
                        64555, 64575, 64590, 64595
                      </td>
                    </tr>
                    <tr data-id="element-1435">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 bg-gray-50" data-id="element-1436">
                        Key Differences
                      </td>
                      <td className="px-6 py-4 text-sm text-red-700 bg-red-50" data-id="element-1437">
                        <ul className="list-disc pl-5 space-y-1" data-id="element-1438">
                          <li data-id="element-1439">Specifically excludes Freedom PNS System</li>
                          <li data-id="element-1440">Classifies as experimental/investigational</li>
                          <li data-id="element-1441">No pathway for coverage</li>
                        </ul>
                      </td>
                      <td className="px-6 py-4 text-sm text-green-700 bg-green-50" data-id="element-1442">
                        <ul className="list-disc pl-5 space-y-1" data-id="element-1443">
                          <li data-id="element-1444">Provides specific coverage criteria</li>
                          <li data-id="element-1445">Includes broader code set</li>
                          <li data-id="element-1446">Recognizes refractory pain indications</li>
                        </ul>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end" data-id="element-1447">
              <button className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 mr-3" onClick={() => setShowComparisonView(false)} data-id="element-1448">
                Close
              </button>
              <button className="px-4 py-2 bg-blue-600 border border-transparent rounded-md shadow-sm text-sm font-medium text-white hover:bg-blue-700" data-id="element-1449">
                Export Comparison
              </button>
            </div>
          </div>
        </div>}
    </div>;
};
export default PolicyAnalysis;